import React from 'react';
import {
  loadTranslatableResourceSection,
  loadTranslatableResources,
  saveTranslatableResources,
  resetErrors,
  getSectionState,
  getSectionNextPage
} from '../../modules/translatableResources'
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import ItemsList from '../../components/ItemsList'
import ItemsListSkeleton from '../../components/ItemsListSkeleton'
import LoadingIndicator from '../../components/LoadingIndicator';
import EmptyView from '../../components/EmptyView';
import OptionsMenu from '../../components/OptionsMenu';
import SolutionsArticleLink from '../../components/SolutionsArticleLink';
import cloneDeep from 'lodash/cloneDeep';
import { withTranslation } from 'react-i18next';
import { withSnackbar } from '../../utils/withSnackbar';
import {
  FIELD_ID_SEPARATOR,
  resourceIdOf,
  resourceName,
  buildTranslatableField,
  buildTranslatableFields,
  rowsFromFlatFields,
  buildTranslatableSaveMap
} from '../../utils/translatableResourceFields';

const sectionStyles = {
  paper: {
    width: '100%',
    marginTop: '20px',
    position: 'relative',
  },
};

/**
 * Page for any section in config/translatableResourceSections.js.
 *
 * Renders either as a flat list of all translatable keys, or as one expandable
 * item per resource - see the section's `expandable` flag.
 */
class TranslatableResources extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      data: [],
      lastCursor: null,
      triggerClearAll: false,
    }
  }

  componentDidMount() {
    this.load(true);
  }

  componentDidUpdate(prevProps) {
    if (this.props.editLanguage !== prevProps.editLanguage) {
      this.setState({ lastCursor: null });
      this.load(true);
    }
  }

  componentWillReceiveProps(newProps) {
    const wasPending = this.props.sectionState.load_pending || this.props.sectionState.save_pending;
    const isPending = newProps.sectionState.load_pending || newProps.sectionState.save_pending;

    if(wasPending === true && isPending === false) {
      this.setState({ data: this.buildData(newProps.resources) });
    }
  }

  isExpandable = () => this.props.section.expandable === true

  load = (resetQuery = false, options = {}) => {
    this.props.loadTranslatableResourceSection(
      this.props.section,
      this.props.editLanguage,
      options,
      resetQuery
    );
  }

  buildData = (resources) => {
    const { t, section } = this.props;

    if(this.isExpandable()) {
      return resources.map(item => ({
        type: section.key,
        id: resourceIdOf(item.shopify_id),
        shopify_id: item.shopify_id,
        progress: typeof item.progress === 'number' ? item.progress : -1,
        // the incoming data is always complete, so it may replace what is on screen
        synchronized: false,
        // display_name is a preview of the resource, which is what an expandable row
        // has to be titled by. The flat list has no row title and does not use it
        title: resourceName(item),
        info: '',
        hasError: item.translations.some(x => x.error === true),
        fields: cloneDeep(item.translations.map(translation =>
          buildTranslatableField(item, translation, translation.key, section)
        )),
      }))
    }

    return [{
      id: section.key,
      title: t(`pages.${section.key}.title`),
      fields: buildTranslatableFields(resources, section)
    }];
  }

  /**
   * Flattens whatever shape the list handed back to {shopify_id, key, target} rows
   */
  collectRows = (items) => {
    if(this.isExpandable()) {
      const rows = [];
      items.forEach(item => {
        item.fields.forEach(field => {
          rows.push({ shopify_id: item.shopify_id, key: field.id, target: field.target })
        })
      })
      return rows;
    }

    if(!items[0] || !items[0].fields) {
      return [];
    }
    return rowsFromFlatFields(this.props.resources, items[0].fields);
  }

  save = (resources) => {
    const { t } = this.props;

    this.props.saveTranslatableResources(this.props.section.key, this.props.editLanguage, resources, res => {
      // success is false only when nothing at all landed, a partial failure is in the counts
      if(!res || !res.data) {
        this.props.enqueueSnackbar(t('translatableResources.saveFailed'), { variant: 'error' });
      } else if(res.data.failed > 0) {
        this.props.enqueueSnackbar(t('translatableResources.savePartiallyFailed', { count: res.data.failed }), { variant: 'error' });
      }
    })
  }

  onSave = items => {
    if(!items || items.length === 0) {
      return;
    }

    const resources = buildTranslatableSaveMap(this.props.resources, this.collectRows(items));

    if(Object.keys(resources).length === 0) {
      return;
    }
    this.save(resources);
  }

  // empties the loaded targets as pending edits only - the user still has to save,
  // and can discard instead. ItemsList hands this to the fields it already renders
  onClearAll = () => {
    this.setState({ triggerClearAll: !this.state.triggerClearAll });
  }

  // a cursor belongs to one resource type, so this pages that type alone rather than
  // going through the whole section the way the initial load does
  onLoadMore = () => {
    const nextPage = getSectionNextPage(this.props.sectionState, this.props.section);
    if(!nextPage || this.state.lastCursor === nextPage.cursor) {
      return;
    }
    this.setState({ lastCursor: nextPage.cursor });
    this.props.loadTranslatableResources(
      this.props.section.key,
      nextPage.resourceType,
      this.props.editLanguage,
      { cursor: nextPage.cursor },
      false
    );
  }

  hasRows = () => this.isExpandable()
    ? this.state.data.length > 0
    : this.state.data.length > 0 && this.state.data[0].fields.length > 0

  renderEmpty = () => {
    const { t, section, sectionState } = this.props;
    return sectionState.load_pending === false
      ? <EmptyView title={t(`pages.${section.key}.noContent`)} />
      : <ItemsListSkeleton rows={5} />
  }

  renderList = () => {
    const { section, sectionState } = this.props;
    const expandable = this.isExpandable();
    const nextPage = getSectionNextPage(sectionState, section);

    if(!expandable && !this.hasRows()) {
      return this.renderEmpty();
    }

    return <ItemsList
      key={`${section.key}-${this.props.editLanguage}`}
      type={section.key}
      showOnlyFieldsList={!expandable}
      clearEditsOnSave={!expandable}
      // no items to sort in the flat list, it is one wrapping item - but its fields sort
      hideSorting={!expandable}
      sortFields={true}
      pagination={false}
      paginate={false}
      useLazyloading
      loadMoreButton={nextPage ? nextPage.cursor : null}
      onLoadMore={this.onLoadMore}
      isPending={sectionState.load_pending || sectionState.save_pending}
      isSubfieldsPending={false}
      isSubfieldsRemoving={false}
      isSaving={sectionState.save_pending}
      saveErrors={sectionState.saveErrors}
      onSave={this.onSave}
      triggerClearAll={this.state.triggerClearAll}
      data={this.state.data}
      forceUpdate={this.state.data.length}
      allowAutoTranslation={true}
      error={sectionState.error}
      emptyMessage={expandable ? this.renderEmpty() : null}
    />
  }

  render() {
    const { t, section, sectionState } = this.props

    return (
      <React.Fragment>
        <Typography variant="h4" gutterBottom component="h2">
          {t(`pages.${section.key}.title`)}

          <OptionsMenu
            onClearAll={this.onClearAll}
            clearAllLabel={t('system.clearAllTranslations')}
            clearAllDanger
            shopPlan={this.props.shopPlan}
          />

          {/* floated, so it lands left of the options menu that was floated before it */}
          {section.solutionsArticle &&
            <SolutionsArticleLink
              article={section.solutionsArticle}
              subject={t(`pages.${section.key}.title`)}
              placement="headingMenu"
            />
          }
        </Typography>

        <Divider className="divider-white" />

        {this.isExpandable() ?
          // an expandable list brings its own surface
          this.renderList()
        :
          <Paper sx={sectionStyles.paper} elevation={1}>
            {this.renderList()}
          </Paper>
        }

        <LoadingIndicator
          pending={
            (sectionState.load_pending && !this.hasRows()) ||
            sectionState.save_pending
          }
          loadingType={sectionState.save_pending ? 'saving' : 'loading'}
          error={sectionState.error}
          onErrorCancel={() => this.props.resetErrors(section.key)}
        />
      </React.Fragment>
    )
  }
}


const mapStateToProps = (state, ownProps) => ({
  sectionState: getSectionState(state, ownProps.section.key),
  resources: getSectionState(state, ownProps.section.key).resources,
  editLanguage: state.shop.shop.edit_language,
  shopPlan: state.shop.shop.charge.name,
  shop: state.shop.shop
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      loadTranslatableResourceSection,
      loadTranslatableResources,
      saveTranslatableResources,
      resetErrors
    },
    dispatch
  )

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withSnackbar(TranslatableResources))))
